<template>
  <common-header-cover-page
    v-if="newsIndex != null"
    :key="$route.fullPath"
    :header-cover-image="newsIndex.headerCoverImage"
    :name="newsIndex.headline"
  >
    <main class="w-full max-w-screen-xl mx-auto px-4 py-8 overflow-x-hidden">
      <common-breadcrumb class="mb-8 lg:mt-8 lg:mb-16" :crumbs="breadCrumbs" />

      <h1 class="mb-8 text-4xl font-bold text-center leading-tight uppercase">
        {{ newsIndex.headline || t('news/title') }}
      </h1>
      <div class="w-full flex justify-end items-center mb-8">
        <form-dropdown
          class="w-full sm:w-1/3 md:w-1/4"
          :label="t('landingpage/index/games/sort_by')"
          :items="newsTypeOptions"
          :item-class="'text-capitalize'"
          :value="currentNewsType"
          @change="handleSelectNewsType"
        />
      </div>

      <news-grid
        v-if="articles.length > 0"
        class="mb-16"
        :articles="articles"
      />
      <div v-if="articles.length > 0" class="flex justify-center">
        <common-button
          size="lg"
          :disabled="isLoadingArticles"
          @click="paginateNews"
        >
          <template v-if="!isLoadingArticles">
            {{ t('general/non_special_texts/load_more') }}
          </template>
          <template v-else>
            {{ t('general/non_special_texts/loading') }}
          </template>
        </common-button>
      </div>
    </main>
  </common-header-cover-page>
</template>

<script setup lang="ts">
import type { BreadCrumb } from '../common/types';
import useNewsIndexPage from './features/useNewsIndexPage';
import useNewsArticles from './features/useNewsArticles';
import { useGoogleBreadcrumb } from '~/helpers/googleBreadcrumb';
import { serializeJsonLd } from '~/helpers/jsonLd';

const { t, locale } = useI18n();
const config = useRuntimeConfig();
const route = useRoute();
const breadCrumbs = ref<Array<BreadCrumb>>([
  { title: t('general/menu/item/news') as string, link: 'news' },
]);

const googleBreadcrumb = computed(() =>
  useGoogleBreadcrumb(
    config.public.websiteUrl,
    locale.value,
    t('app/home'),
    breadCrumbs.value,
    route.path,
  ),
);

useHead({
  script: () => {
    const scripts = [];
    if (googleBreadcrumb.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleBreadcrumb.value),
      });
    }
    return scripts;
  },
});

const { newsIndex } = await useNewsIndexPage();

const {
  handleSelectNewsType,
  paginateNews,
  articles,
  currentNewsType,
  newsTypeOptions,
  isLoadingArticles,
} = await useNewsArticles();
</script>
